import React, { useMemo, useState } from "react";
import { ADVERTISER, OVERVIEW, GAPS, showUpCellClass, SHOW_UP_LEGEND } from "../../../../../core/theme/reportTheme";
import { advertiserGridTable, gridSortColumns, defaultGridCompare } from "../../../../../core/data/advertiserGrid";
import { sortRows, nextSort } from "../../../../../core/data/sortRows";
import { cellText, gridSummaryText, CELL_STATUS_WORDS } from "../../../../../core/data/advertiserOverviewText";
import { CHANNEL_NAMES } from "../../../../../core/data/marketOverviewText";
import SortButton from "../../../components/SortButton";

const MEASURES = [["any", "Any channel"], ...["ppc", "local", "seo"].map(c => [c, CHANNEL_NAMES[c]])];

// "Where you show up": postcodes as rows (town underneath), the top keywords
// as columns (every keyword when expanded), plus Any keyword. Styled like
// Market Overview's "Gaps by postcode". Opens with the postcodes not seen
// where ads ran first; every heading sorts.
export default function ShowUpGrid({ result, expandButton, expanded }) {
  const [measure, setMeasure] = useState("any");
  const [sort, setSort] = useState(null);
  const table = useMemo(() => advertiserGridTable(result, { measure, allKeywords: expanded }), [result, measure, expanded]);
  const columns = useMemo(() => gridSortColumns(table.keywords), [table.keywords]);
  // Collapsing can hide the sorted keyword column; fall back to the default then.
  const activeSort = sort && columns[sort.column] ? sort : null;
  const rows = useMemo(() => sortRows(table.rows, activeSort, columns, defaultGridCompare), [table.rows, activeSort, columns]);
  const sortProps = { sort: activeSort, onSort: column => setSort(nextSort(activeSort, column, columns)) };
  const allKeywords = result.gridSummary.keywordsBySearches.length;
  const summary = measure === "any" ? result.gridSummary : table.summary;

  return (
    <div className={`${OVERVIEW.panel} flex-1`}>
      <div className={OVERVIEW.panelHeader}>
        <h3 className={OVERVIEW.panelTitle}>Where you show up</h3>
        <div className="flex items-center gap-2 shrink-0">
          <div className={GAPS.toggle}>
            {MEASURES.map(([id, label]) => (
              <button key={id} type="button" onClick={() => setMeasure(id)} className={measure === id ? GAPS.toggleActive : GAPS.toggleButton}>{label}</button>
            ))}
          </div>
          {expandButton}
        </div>
      </div>
      <div className={GAPS.summaryRow}>
        <span className="truncate">{gridSummaryText(summary)}</span>
        <span className="flex items-center gap-3 shrink-0">
          {!expanded && allKeywords > table.keywords.length && <span>Top {table.keywords.length} of {allKeywords} keywords · ⤢ for all</span>}
          {activeSort && <button type="button" onClick={() => setSort(null)} className={OVERVIEW.link}>Back to default order</button>}
        </span>
      </div>
      <div className={OVERVIEW.panelScroll}>
        <table className="w-full border-separate border-spacing-0 table-fixed">
          <thead>
            <tr>
              <th className={ADVERTISER.gridThFirst}>
                <SortButton column="postcode" label="Postcode" {...sortProps} />
                <span className="text-slate-300"> / </span>
                <SortButton column="town" label="Town" {...sortProps} />
              </th>
              {table.keywords.map(k => (
                <th key={k} className={ADVERTISER.gridTh} title={k}>
                  <span className="block truncate"><SortButton column={`kw:${k}`} label={k} {...sortProps} /></span>
                </th>
              ))}
              <th className={ADVERTISER.gridTh}><SortButton column="any" label="Any keyword" {...sortProps} /></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.postcode}>
                <td className={ADVERTISER.gridRowLabel}>
                  <p className={ADVERTISER.gridPostcode}>{row.postcode}</p>
                  {row.town && <p className={ADVERTISER.gridTown} title={row.town}>{row.town}</p>}
                </td>
                {row.cells.map(cell => <Cell key={cell.keyword} cell={cell} postcode={row.postcode} />)}
                <Cell cell={row.any} postcode={row.postcode} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={ADVERTISER.legendRow}>
        <span><span className={`${ADVERTISER.legendSwatch} ${SHOW_UP_LEGEND.seen}`} />seen (darker = in more of the searches)</span>
        <span><span className={`${ADVERTISER.legendSwatch} ${SHOW_UP_LEGEND.notSeenAdsRan}`} />not seen · ads ran</span>
        <span><span className={`${ADVERTISER.legendSwatch} ${SHOW_UP_LEGEND.notSeenNoAds}`} />not seen · no ads</span>
        <span>– no completed searches</span>
      </div>
    </div>
  );
}

function Cell({ cell, postcode }) {
  const where = `${postcode} · ${cell.keyword || "any keyword"}`;
  const title = cell.searches === 0
    ? `${where}: no completed searches`
    : `${where}: ${CELL_STATUS_WORDS[cell.status]} · seen in ${cell.value} of ${cell.searches} searches · ads ran in ${cell.searchesWithAds}`;
  return (
    <td className={ADVERTISER.gridCell}>
      <div className={showUpCellClass(cell.status, cell.value, cell.searches)} title={title}>
        {cellText(cell.value, cell.searches)}
      </div>
    </td>
  );
}
